// The upload queue for proof photos. A photo is stored on THIS DEVICE (IndexedDB) the moment it
// is picked, so nothing the treasurer does ever waits on the network (CLAUDE.md invariant 8):
// the expense row is already saved; the photo follows whenever there is a connection, with
// growing delays between tries, surviving page reloads and a closed browser.
import { createStore, del, entries, get, set } from 'idb-keyval'
import { currentUserId } from '@/data/auth'
import { createPendingAttachment, markAttachmentReady, uploadProofObject } from '@/data/attachments'
import type { ProofParent } from '@/data/attachments'
import { MAX_ATTEMPTS, isNetworkFailure, isPermanentFailure, retryDelayMs } from './pure'
import type { QueueStatus } from './pure'

export interface QueueEntry {
  id: string
  /** Whose photo it is: only that person's session may upload it. */
  userId: string
  /** null for the church treasury's own proofs. */
  departmentId: string | null
  parent: ProofParent
  blob: Blob
  thumb: Blob
  mime: string
  sizeBytes: number
  sha256: string
  storagePath: string
  thumbPath: string
  status: QueueStatus
  /** Every try, for the waiting time between tries. */
  attempts: number
  /** Tries the SERVER answered with an error. Only these count towards giving up; a dropped connection never does. */
  serverFailures: number
  /** When it was added, so photos upload oldest first. */
  addedAt: number
  nextAttemptAt: number
  lastError: string | null
}

let store: ReturnType<typeof createStore> | null = null
const db = () => (store ??= createStore('church-accounts-attachments', 'queue'))

type Listener = () => void
const listeners = new Set<Listener>()
const notify = () => listeners.forEach((l) => l())

/** Re-render whenever the queue changes. Returns an unsubscribe function. */
export function subscribeQueue(l: Listener): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}

export async function enqueuePhoto(
  e: Omit<QueueEntry, 'status' | 'attempts' | 'serverFailures' | 'addedAt' | 'nextAttemptAt' | 'lastError'>,
): Promise<void> {
  await set(e.id, { ...e, status: 'queued', attempts: 0, serverFailures: 0, addedAt: Date.now(), nextAttemptAt: 0, lastError: null } satisfies QueueEntry, db())
  notify()
  void processQueue()
}

export async function listQueue(parentId?: string): Promise<QueueEntry[]> {
  const all = (await entries<string, QueueEntry>(db())).map(([, v]) => v)
  return all.filter((e) => !parentId || e.parent.id === parentId).sort((a, b) => (a.addedAt ?? 0) - (b.addedAt ?? 0) || a.id.localeCompare(b.id))
}

/** Forget photos waiting for a record that no longer exists (a discarded draft). */
export async function removeQueuedFor(parentId: string): Promise<void> {
  for (const e of await listQueue(parentId)) await del(e.id, db())
  notify()
}

/** Give a failed photo another go. */
export async function retryEntry(id: string): Promise<void> {
  const e = await get<QueueEntry>(id, db())
  if (!e) return
  await set(id, { ...e, status: 'queued', attempts: 0, serverFailures: 0, nextAttemptAt: 0, lastError: null }, db())
  notify()
  void processQueue({ force: true })
}

async function attempt(e: QueueEntry): Promise<void> {
  // 1. the row (so the file has a legitimate home), 2. the files, 3. flip the row to ready.
  await createPendingAttachment({
    id: e.id,
    departmentId: e.departmentId,
    parent: e.parent,
    storagePath: e.storagePath,
    thumbPath: e.thumbPath,
    mimeType: e.mime,
    sizeBytes: e.sizeBytes,
    sha256: e.sha256,
  })
  await uploadProofObject(e.storagePath, e.blob, e.mime)
  await uploadProofObject(e.thumbPath, e.thumb, e.thumb.type || 'image/webp')
  await markAttachmentReady(e.id)
}

let running: Promise<void> | null = null
let rerun = false
let rerunForce = false

/**
 * Try every photo that is due. Only one pass runs at a time. `force` ignores the waiting time
 * (used by the "Try now" button and when the connection comes back).
 */
export function processQueue(opts: { force?: boolean } = {}): Promise<void> {
  if (running) {
    rerun = true
    rerunForce ||= Boolean(opts.force) // a "try now" tapped during a pass must not be downgraded
    return running
  }
  running = (async () => {
    try {
      let force = Boolean(opts.force)
      do {
        rerun = false
        force ||= rerunForce
        rerunForce = false
        const now = Date.now()
        let me: string
        try {
          me = await currentUserId()
        } catch {
          break // nobody is signed in: keep everything on the device and wait
        }
        for (let e of await listQueue()) {
          if (e.status !== 'queued' || e.userId !== me) continue
          if (!force && e.nextAttemptAt > now) continue
          try {
            await attempt(e)
            await del(e.id, db())
          } catch (err) {
            // Work from the entry as it is NOW: while we were uploading, the treasurer may have discarded
            // its expense (so it must not come back) or tapped "Try again" (so keep that reset).
            const latest = await get<QueueEntry>(e.id, db())
            if (!latest) {
              notify()
              continue
            }
            e = latest
            const attempts = e.attempts + 1
            const failure = err as { status?: number; code?: string; message?: string }
            // Being offline is not a reason to give up on a receipt: keep waiting, with growing delays.
            // Only the server refusing it (or answering with an error too many times) ends the retries.
            const serverFailures = (e.serverFailures ?? 0) + (isNetworkFailure(failure) ? 0 : 1)
            const giveUp = isPermanentFailure(failure) || serverFailures >= MAX_ATTEMPTS
            await set(
              e.id,
              {
                ...e,
                attempts,
                serverFailures,
                status: giveUp ? 'failed' : 'queued',
                nextAttemptAt: Date.now() + retryDelayMs(attempts),
                lastError: (err as { message?: string })?.message ?? 'Upload failed',
              } satisfies QueueEntry,
              db(),
            )
          }
          notify()
        }
      } while (rerun)
    } finally {
      running = null
    }
  })()
  return running
}

let started = false
/** Call once when the app starts: resume unfinished uploads, and keep trying quietly. */
export function startUploadQueue(): void {
  if (started || typeof window === 'undefined') return
  started = true
  void processQueue({ force: true })
  window.addEventListener('online', () => void processQueue({ force: true }))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void processQueue()
  })
  window.setInterval(() => void processQueue(), 15_000)
}
